<template>
  <div>
    <!-- 無障礙跳轉到主要內容 -->
    <a href="#main-content" class="skip-link">
        {{ $t("skipToMain") }}
    </a>
    <nav v-if="shouldShowNavbar" class="navbar navbar-expand-lg custom-navbar" :class="{ 'white-background': shouldHaveWhiteBackground || isNavbarOpen  }">
      <div class="container-fluid">
        <router-link
          :to="isShowTP ? { name: 'TimePortrait' } : { path: '/' }"
          class="navbar-brand custom-logo"
        >
          <img src="/images/logo.png" alt="LOGO" class="logo-img">
        </router-link>
        
        <!-- Vue控制的手機版切換按鈕 -->
        <button
          class="navbar-toggler"
          type="button"
          @click="toggleNavbar"
          aria-label="Toggle navigation"
        >
          <i class="bi bi-list text-white"></i>
        </button>

        <!-- Vue控制的導航選單 -->
        <div 
          id="navbarNav" 
          class="navbar-collapse"
          :class="{ 'show': isNavbarOpen }"
        >
          <!-- 首頁功能導航區 - 只在首頁顯示 -->
          <div v-if="isHomePage" class="navbar-nav mx-auto d-lg-none">
            <div class="home-nav-buttons-wrapper">
              <button 
                v-for="(feature, index) in homeFeatures" 
                :key="index" 
                :class="['btn block-btn home-nav-feature-btn', { active: activeHomeFeature === index }]" 
                @click="scrollToHomeFeature(index)"
              >
                {{ feature.name }}
              </button>
            </div>
          </div>

          <!-- 中間的按鈕組 - 只有登入後且非首頁才顯示 -->
          <div v-else-if="isLoggedIn" class="navbar-nav mx-auto">
            <div class="nav-button-group" v-if="!isShowTP" >
              <router-link 
                v-if="canInference" 
                to="/generation" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("imageGeneration") }}
              </router-link>
              <router-link 
                v-if="canInference" 
                to="/upscale" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("imageUpscale") }}
              </router-link>
              <router-link 
                v-if="canInference" 
                to="/inpainting" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("imageInpainting") }}
              </router-link>
              <router-link 
                v-if="currentUser == 'Admin'" 
                to="/timePortrait" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("timePortrait") }}
              </router-link>
              <router-link 
                v-if="canAnnotation" 
                to="/check" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("imageChecker") }}
              </router-link>
              <router-link 
                v-if="canManage" 
                to="/users" 
                class="nav-btn"
                active-class="active"
                @click="closeNavbarOnMobile"
              >
                {{ $t("userManagement") }}
              </router-link>
            </div>
          </div>
          
          <!-- 右邊控制區 -->
          <ul class="navbar-nav align-items-center" :class="{ 'ms-auto' : isHomePage || isLoginPage || isUserTermsPage }">
            <!-- 語言切換 -->
            <li class="nav-item dropdown me-3">
              <button
                type="button"
                class="btn block-btn"
                @click="toggleLang"
                @keydown.enter.prevent="toggleLang"
                aria-expanded="false"
              >
                <i class="bi bi-globe2"></i>
                &nbsp;{{ $t("lang") }}
              </button>
              <ul 
                class="dropdown-menu dropdown-menu-end"
                :class="{ 'show': isLanguageDropdownOpen }"
              >
                <li><a class="dropdown-item" @click="changeLanguage('en')">EN</a></li>
                <li><a class="dropdown-item" @click="changeLanguage('zh')">中文</a></li>
              </ul>
            </li>

            <!-- 登入狀態：圖標、使用者名稱、登出按鈕組 -->
            <li v-if="isLoggedIn" class="nav-item">
              <div class="user-info-group">
                <img class="custom-icon" src="/images/ellipse_red.png" alt="">
                <span class="custom-username">{{ currentUser }}</span>
                <button class="custom-logout-btn" @click="onLogout" :aria-label="$t('logout')">
                  <img class="custom-icon" src="/images/logout.png" alt="">
                </button>
              </div>
            </li>

            <!-- 未登入狀態：登入按鈕 -->
            <li v-else class="nav-item">
              <router-link to="/login" class="btn block-btn" @click="closeNavbarOnMobile">
                {{ $t("login") }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- 點擊遮罩關閉選單 -->
    <div 
      v-if="isNavbarOpen" 
      class="navbar-backdrop"
      @click="closeNavbar"
    ></div>

    <router-view id="main-content" @home-feature-changed="onHomeFeatureChanged" />
  </div>
</template>

<script>
import router from './router'
import { isLoggedIn, userPermissions, currentUser, resetAuthState } from '@/auth'
import { clearTokens } from '@/utils/token';

export default {
  name: 'App',
  data() {
    return {
      isNavbarOpen: false,
      isLanguageDropdownOpen: false,
      activeHomeFeature: 0,
    }
  },
  computed: {
    // Options API 下，把 ref.value 拿出來給 template 用
    isLoggedIn() {
      return isLoggedIn.value
    },
    isHomePage() {
      return this.$route.path === '/'
    },
    isLoginPage() {
      return this.$route.path === '/login'
    },
    isUserTermsPage() {
      return this.$route.path === '/userterms'
    },
    shouldHaveWhiteBackground() {
      // 除了首頁和登入頁以外都用白色背景
      return !this.isHomePage && !this.isLoginPage
    },
    canManage() {
      return userPermissions.value.enable_user_manage
    },
    canTrain() {
      return userPermissions.value.enable_train
    },
    canInference() {
      return userPermissions.value.enable_inference
    },
    canAnnotation() {
      return userPermissions.value.enable_annotation
    },
    currentUser() {
      return currentUser.value
    },
    // 動態生成首頁功能列表
    homeFeatures() {
      return [
        { name: this.$t('imageGenerationTitle'), id: 'feature-0' },
        { name: this.$t('imageUpscaleTitle'), id: 'feature-1' },
        { name: this.$t('imageInpaintingTitle'), id: 'feature-2' },
        { name: this.$t('imageCheckerTitle'), id: 'feature-3' }
      ]
    },
    shouldShowNavbar() {
      return !(this.$route.meta && this.$route.meta.hideNavbar)
    },
    isShowTP() {
        return (this.currentUser === 'showTP' || this.currentUser === 'onlyCanal' || this.currentUser === 'InfoTech2026')
    },
  },
  mounted() {
    this.updateHtmlLang(this.$i18n.locale)
    // 監聽視窗大小變化，當從手機版切換到桌面版時自動關閉選單
    window.addEventListener('resize', this.handleResize)
    // 監聽點擊事件，點擊選單外部時關閉選單
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    '$i18n.locale'(newLocale) {
      this.updateHtmlLang(newLocale)
    },
    // 監聽路由變化，切換頁面時關閉選單
    '$route'() {
      this.closeNavbar()
    }
  },
  methods: { 
    updateHtmlLang(locale) {
      const langMap = {
        'zh': 'zh-TW',  // 中文 -> 繁體中文（台灣）
        'en': 'en'      // 英文
      }
      const htmlLang = langMap[locale] || 'zh-TW'
      document.documentElement.lang = htmlLang
    },
    // Navbar 控制方法
    toggleNavbar() {
      this.isNavbarOpen = !this.isNavbarOpen
      // 關閉語言下拉選單
      this.isLanguageDropdownOpen = false
    },
    
    closeNavbar() {
      this.isNavbarOpen = false
      this.isLanguageDropdownOpen = false
    },
    
    closeNavbarOnMobile() {
      // 只在手機版時關閉選單
      if (window.innerWidth < 992) {
        this.closeNavbar()
      }
    },
    
    // 語言下拉選單控制
    toggleLanguageDropdown() {
      this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen
    },
    
    // 處理視窗大小變化
    handleResize() {
      if (window.innerWidth >= 992) {
        this.closeNavbar()
      }
    },
    
    // 處理點擊外部區域
    handleClickOutside(event) {
      const navbar = this.$el.querySelector('.navbar')
      const dropdown = this.$el.querySelector('.dropdown')
      
      if (navbar && !navbar.contains(event.target)) {
        this.closeNavbar()
      } else if (dropdown && !dropdown.contains(event.target)) {
        this.isLanguageDropdownOpen = false
      }
    },

    // 首頁功能導航方法
    scrollToHomeFeature(index) {
      this.activeHomeFeature = index
      this.closeNavbarOnMobile() // 手機版點擊後關閉選單
      
      // 等待下一個 tick 確保 DOM 更新完成
      this.$nextTick(() => {
        const element = document.getElementById(this.homeFeatures[index].id)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },

    // 接收來自 HomePage 的特徵變更事件
    onHomeFeatureChanged(activeIndex) {
      this.activeHomeFeature = activeIndex
    },
    
    // 原有方法
    onLogout() {
      if (confirm(this.$t('confirmLogout'))) {
        clearTokens()      // access + refresh + permissions 一次清光
        resetAuthState()   // 把 isLoggedIn / userPermissions / currentUser 清掉
        this.closeNavbar()
        router.push({ name: 'Home' })
      }
    },
    toggleLang() {
      this.changeLanguage(this.$i18n.locale === 'en' ? 'zh' : 'en')
    },
    changeLanguage(lang) {
      this.$i18n.locale = lang
      this.isLanguageDropdownOpen = false // 選擇語言後關閉下拉選單
      this.closeNavbarOnMobile() // 手機版選擇語言後關閉整個選單
    },
  }
}
</script>

<style scoped>
.skip-link {
  position: absolute;
  top: -8rem;
  background-color: #fff;
  padding: 40px;
  z-index: 200;
}    
.skip-link:focus {
    top: 0;
    box-shadow: 0 0 0 2px #000, 0 0 0 4px #fff;
}
/* 自訂導航欄樣式 */
.custom-navbar {
  background-color: transparent;
  padding: 24px 10px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: background-color 0.3s ease;
}

/* 白色背景樣式 */
.custom-navbar.white-background {
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.custom-logo {
  font-weight: bold;
  font-size: 1.2rem;
  color: #333;
  text-decoration: none;
  margin: 0;
  padding: 0;
}

.custom-logo:hover {
  color: #333;
  text-decoration: none;
}

.logo-img {
  height: 50px;
  width: auto;
}

/* Navbar Toggler 樣式 */
.navbar-toggler {
  background: #000;
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  padding: 4px 4px;
  margin-left: auto;
}
.navbar-toggler:focus {
  box-shadow: none;
}
.navbar-toggler i {
  font-size: 24px;
}

/* Navbar Collapse 樣式 */
.navbar-collapse {
  display: flex;
  flex-basis: auto;
  flex-grow: 1;
  align-items: center;
}

/* 手機版遮罩 */
.navbar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 90;
  display: none;
}

/* 中間按鈕組容器 */
.nav-button-group {
  display: flex;
  background-color: #212121;
  border-radius: 25px;
  padding: 4px;
  gap: 2px;
}

/* 首頁功能導航按鈕組樣式 */
.home-nav-buttons-wrapper {
  display: flex;
  justify-content: center;
  background-color: #212121;
  border-radius: 25px;
  padding: 4px;
  width: fit-content;
  margin: 0 auto;
  gap: 2px;
}

.home-nav-feature-btn {
  background: transparent;
  color: #ffffff;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
}

.home-nav-feature-btn:hover {
  color: #e0e0e0;
}

.home-nav-feature-btn.active {
  background-color: #ffffff;
  color: #000000;
  font-weight: 500;
}

/* 導航按鈕樣式 */
.nav-btn {
  padding: 8px 16px;
  border-radius: 20px;
  text-decoration: none;
  color: #ffffff;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-btn:hover {
  color: #e0e0e0;
  text-decoration: none;
}

.nav-btn.active {
  background-color: #ffffff;
  color: #000000;
}

/* Dropdown 樣式 */
.dropdown {
  position: relative;
  display: flex;
  flex-direction: column;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  display: none;
  min-width: 10rem;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  font-size: 0.875rem;
  color: #212529;
  text-align: left;
  list-style: none;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.25rem 1rem;
  clear: both;
  font-weight: 400;
  color: #212529;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  cursor: pointer;
}

.dropdown-item:hover {
  color: #1e2125;
  background-color: #e9ecef;
}

/* 使用者資訊組合 */
.user-info-group {
  background-color: #212121;
  padding: 5px 5px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-icon {
  width: 30px;
}

.custom-username {
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
}

.custom-logout-btn {
  background: none;
  border: none;
  color: #ffffff;
  font-size: 0.9rem;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s ease;
}

.custom-logout-btn:hover {
  color: #e0e0e0;
}

/* 響應式設計 */
@media (max-width: 991.98px) {
  .navbar-collapse {
    display: none;
    width: 100%;
  }
  
  .navbar-collapse.show {
    display: block;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #212121;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    padding: 1rem;
    z-index: 95;
  }
  
  .navbar-backdrop {
    display: block;
  }
  
  .navbar-nav.mx-auto {
    margin: 1rem 0 !important;
  }
  
  .nav-button-group,
  .home-nav-buttons-wrapper {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .nav-btn,
  .home-nav-feature-btn {
    width: 100%;
    text-align: center;
    margin-bottom: 0.25rem;
  }
  
  .user-info-group {
    justify-content: center;
    margin: 10px 0;
  }
  
  .dropdown-menu {
    position: static;
    width: 100%;
    margin-top: 0.5rem;
    border: none;
    box-shadow: none;
    background-color: #f8f9fa;
  }
}

@media (max-width: 768px) {
  .logo-img {
    height: 45px;
  }
  .navbar-toggler {
    width: 45px;
    height: 45px;
  }
  .nav-button-group,
  .home-nav-buttons-wrapper {
    flex-direction: column;
  }
  
  .nav-btn,
  .home-nav-feature-btn {
    width: 100%;
    text-align: center;
  }
}

/* 桌面版時確保正常顯示 */
@media (min-width: 992px) {
  .navbar-collapse {
    display: flex !important;
  }
  
  .navbar-backdrop {
    display: none !important;
  }
}
</style>
